import { useState, useEffect, useCallback } from 'preact/hooks';
import { onAuthChange } from '../auth/google-auth';
import { authFetch } from '../api/authFetch';
import { useSocket } from '../hooks/useSocket';
import { useMemos } from '../hooks/useMemos';
import { useMemoRecorder } from '../hooks/useMemoRecorder';
import { useAttachments } from '../hooks/useAttachments';
import { useSettingsStore } from '../state/settings.store';
import type { VoiceMemoSummary, VoiceMemoGroupSummary, VoiceMemoStats, ProjectPlanSummary, PlanChunkSummary } from '@voice-assistant/shared';
import { MarkdownContent } from '../shared-ui/MarkdownContent';
import { ReaderToggle } from './ReaderMode/ReaderToggle';
import { ReaderSurface } from './ReaderMode/ReaderSurface';

function formatDuration(ms: number | null): string {
  if (!ms) return '--';
  const secs = Math.floor(ms / 1000);
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return mins > 0 ? `${mins}:${String(rem).padStart(2, '0')}` : `${rem}s`;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const STATUS_COLORS: Record<string, string> = {
  recorded: '#f59e0b', transcribing: '#3b82f6', transcribed: '#10b981',
  processing: '#8b5cf6', planned: '#06b6d4', error: '#ef4444',
};

function ProjectPills({ tags, onEdit }: { tags: string[] | null; onEdit?: () => void }) {
  if (!tags?.length) return null;
  return (
    <div class="memo-project-pills" onClick={(e) => { if (onEdit) { e.stopPropagation(); onEdit(); } }}>
      {tags.map((t) => <span key={t} class="memo-project-pill">{t}</span>)}
      {onEdit && <span class="memo-project-pill memo-pill-edit" title="Edit tags">&#9998;</span>}
    </div>
  );
}

function MemoCard({ memo, onTranscribe, onProcess, onDelete, onPlay, onExpand, onRetriage, onEditTags, onDistill, playingId, busyId, expanded }: {
  memo: VoiceMemoSummary; onTranscribe: (id: string) => void; onProcess: (id: string) => void;
  onDelete: (id: string) => void; onPlay: (id: string) => void; onExpand: (id: string) => void;
  onRetriage: (id: string) => void; onEditTags: (id: string) => void; onDistill: (id: string) => void;
  playingId: string | null; busyId: string | null; expanded: boolean;
}) {
  const isPlaying = playingId === memo.id;
  const isBusy = busyId === memo.id || memo.status === 'transcribing' || memo.status === 'processing';

  return (
    <div class={`memo-card${expanded ? ' expanded' : ''}`} onClick={() => onExpand(memo.id)}>
      <div class="memo-card-header">
        <div class="memo-card-left">
          <span class="memo-status-dot" style={{ background: STATUS_COLORS[memo.status] || '#666' }} title={memo.status} />
          <span class="memo-status-label">{memo.status}</span>
          <span class="memo-duration">{formatDuration(memo.durationMs)}</span>
          <span class="memo-size">{formatSize(memo.audioSizeBytes)}</span>
          {memo.imageCount > 0 && <span class="memo-images-badge">📎{memo.imageCount}</span>}
        </div>
        <span class="memo-time">{timeAgo(memo.createdAt)}</span>
      </div>

      <ProjectPills tags={memo.projectTags} onEdit={expanded ? () => onEditTags(memo.id) : undefined} />

      <div class="memo-card-body">
        {!memo.transcript ? (
          <div class="memo-untranscribed">Untranscribed memo</div>
        ) : !expanded ? (
          <div class="memo-transcript">{memo.transcript.length > 120 ? memo.transcript.slice(0, 120) + '…' : memo.transcript}</div>
        ) : (
          <div class="memo-ab-compare">
            <div class="memo-raw">
              <div class="memo-raw-header">Raw transcript</div>
              <div class="memo-raw-text">{memo.transcript}</div>
            </div>
            {memo.distilledTranscript && (
              <div class="memo-distilled">
                <div class="memo-distilled-header">Distilled (Gemma)</div>
                <div class="memo-distilled-text">{memo.distilledTranscript}</div>
              </div>
            )}
          </div>
        )}
        {memo.errorMessage && <div class="memo-error">{memo.errorMessage}</div>}
      </div>

      {/* Attached images — shown when expanded */}
      {expanded && memo.imageCount > 0 && (
        <div class="memo-images">
          {Array.from({ length: memo.imageCount }, (_, i) => (
            <img key={i} src={`/api/memos/${memo.id}/image/${i}`} class="memo-image-thumb" alt={`Attachment ${i + 1}`} loading="lazy" />
          ))}
        </div>
      )}

      {expanded && memo.aiSummary && (
        <div class="memo-plan">
          <div class="memo-plan-header">
            AI Plan
            <ReaderToggle surfaceId={`memo-plan-${memo.id}`} />
          </div>
          <ReaderSurface id={`memo-plan-${memo.id}`}>
            <div data-reader-block-id="plan-content">
              <MarkdownContent text={memo.aiSummary} class="memo-plan-content" />
            </div>
          </ReaderSurface>
        </div>
      )}

      <div class="memo-card-actions" onClick={(e) => e.stopPropagation()}>
        <button class={`memo-btn memo-btn-play${isPlaying ? ' active' : ''}`} onClick={() => onPlay(memo.id)} title={isPlaying ? 'Stop' : 'Play'}>
          {isPlaying ? '⏹' : '▶'}
        </button>
        {(memo.status === 'recorded' || memo.status === 'error') && (
          <button class="memo-btn memo-btn-transcribe" onClick={() => onTranscribe(memo.id)} disabled={isBusy} title="Transcribe">
            {isBusy ? '…' : '📝'}
          </button>
        )}
        {memo.transcript && (
          <button class="memo-btn memo-btn-retriage" onClick={() => onRetriage(memo.id)} disabled={isBusy} title="Re-triage (re-classify project)">
            🏷
          </button>
        )}
        {memo.transcript && !memo.distilledTranscript && (
          <button class="memo-btn memo-btn-distill" onClick={() => onDistill(memo.id)} disabled={isBusy} title="Distill (clean up transcript via Gemma)">
            {isBusy ? '…' : '✨'}
          </button>
        )}
        {memo.status === 'transcribed' && (
          <button class="memo-btn memo-btn-process" onClick={() => onProcess(memo.id)} disabled={isBusy} title="Add to plan">
            {isBusy ? '…' : '📌'}
          </button>
        )}
        <button class="memo-btn memo-btn-delete" onClick={() => { if (confirm('Delete this memo?')) onDelete(memo.id); }} title="Delete">🗑</button>
      </div>
    </div>
  );
}

function formatGroupDate(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 0) return `Today ${timeStr}`;
  if (diffDays === 1) return `Yesterday ${timeStr}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${timeStr}`;
}

function GroupCard({ group, onPlan, onPromote, onDelete, onExpand, onAskArchitect, expanded }: {
  group: VoiceMemoGroupSummary; onPlan: (id: string) => void; onPromote: (id: string) => void;
  onDelete: (id: string) => void; onExpand: (id: string) => void;
  onAskArchitect?: (projectKey: string, context: string) => void;
  expanded: boolean;
}) {
  const isPlanning = group.status === 'planning';
  const isPlanned = group.status === 'planned';
  const isStale = group.status === 'stale';

  return (
    <div class={`memo-card group-card${expanded ? ' expanded' : ''}${isPlanned ? ' group-planned' : ''}${isStale ? ' group-stale' : ''}`} onClick={() => onExpand(group.id)}>
      <div class="memo-card-header">
        <div class="memo-card-left">
          <span class="memo-status-dot" style={{ background: isPlanned ? '#06b6d4' : isStale ? '#f59e0b' : isPlanning ? '#8b5cf6' : '#f59e0b' }} />
          <span class="group-name">{group.name}</span>
          <span class="group-memo-count">{group.memoCount}</span>
        </div>
        <div class="group-card-right">
          <span class="memo-status-label">{group.status}</span>
          <span class="group-date">{formatGroupDate(group.createdAt)}</span>
        </div>
      </div>

      {/* Summary line — always visible */}
      {group.summary && (
        <div class="group-summary">{group.summary}</div>
      )}

      <ProjectPills tags={group.projectTags} />

      {/* Show memo previews when expanded */}
      {expanded && group.memos && (
        <div class="group-memos-list">
          {group.memos.map((m, i) => (
            <div key={m.id} class="group-memo-preview">
              <span class="group-memo-idx">{i + 1}.</span>
              <span class="group-memo-text">{m.transcript?.slice(0, 80) || m.title || 'Untranscribed'}{(m.transcript?.length || 0) > 80 ? '…' : ''}</span>
              <span class="memo-time">{timeAgo(m.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Group plan — shown when expanded and plan exists */}
      {expanded && group.aiGroupPlan && (
        <div class="memo-plan">
          <div class="memo-plan-header">
            Group Plan
            {group.planCostUsd != null && <span class="plan-cost">${group.planCostUsd.toFixed(4)}</span>}
            <ReaderToggle surfaceId={`memo-plan-${group.id}`} />
          </div>
          <ReaderSurface id={`memo-plan-${group.id}`}>
            <div data-reader-block-id="plan-content">
              <MarkdownContent text={group.aiGroupPlan} class="memo-plan-content" />
            </div>
          </ReaderSurface>
        </div>
      )}

      {isStale && (
        <div class="memo-stale-banner">Plan is stale — a memo was removed. Re-plan to update.</div>
      )}

      <div class="memo-card-actions" onClick={(e) => e.stopPropagation()}>
        {(!isPlanned || isStale) && (
          <button class="memo-btn memo-btn-process" onClick={() => onPlan(group.id)} disabled={isPlanning} title={isStale ? 'Re-plan group' : 'Generate Group Plan'}>
            {isPlanning ? '…' : isStale ? '🧠 Re-plan' : '🧠 Plan'}
          </button>
        )}
        {(isPlanned || group.aiGroupPlan) && (
          <button class="memo-btn memo-btn-promote" onClick={() => onPromote(group.id)} title="Open Claude session with this plan">
            💬 Chat
          </button>
        )}
        {onAskArchitect && group.projectTags?.length === 1 && (isPlanned || group.aiGroupPlan) && (
          <button class="memo-btn memo-btn-architect" onClick={() => {
            const context = `I'm looking at a memo group "${group.name}" for project ${group.projectTags[0]}.\n\n` +
              (group.aiGroupPlan ? `**Group Plan:**\n${group.aiGroupPlan.slice(0, 2000)}\n\n` : '') +
              `The group has ${group.memoCount} memo(s) and status is "${group.status}".\n\nPlease review this and share your thoughts — what should we prioritize, any concerns, or suggestions for the plan?`;
            onAskArchitect(group.projectTags[0], context);
          }} title="Discuss this group with the project's Architect">
            🏗️ Ask
          </button>
        )}
        <button class="memo-btn memo-btn-delete" onClick={() => { if (confirm('Delete this group?')) onDelete(group.id); }} title="Delete Group">🗑</button>
      </div>
    </div>
  );
}

const RISK_COLORS: Record<string, string> = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };
const CHUNK_STATUS_ICONS: Record<string, string> = {
  pending: '○', 'in-progress': '●', done: '✓', failed: '✗', 'needs-review': '?', deferred: '—',
};

function PlanCard({ plan, onGenerate, onDelete, onExpand, onExecuteChunk, onChunkStatus, onBulkChunkStatus, onExecuteAll, onSkipFailuresAndContinue, onDiscussPlan, onDiscussChunkResult, onMergeBranch, onDiscardBranch, onResolveConflicts, expanded, generating, executingChunkId, executingAll, autoReplanThreshold }: {
  plan: ProjectPlanSummary; onGenerate: (projectKey: string) => void;
  onDelete: (id: string) => void; onExpand: (id: string) => void;
  onExecuteChunk: (chunkId: string, title: string) => void;
  onChunkStatus: (chunkId: string, status: string) => void;
  onBulkChunkStatus: (chunkIds: string[], status: string) => void;
  onExecuteAll: (planId: string) => void;
  onSkipFailuresAndContinue: (planId: string) => void;
  onDiscussPlan?: (projectKey: string, context: string) => void;
  onDiscussChunkResult?: (projectKey: string, context: string) => void;
  onMergeBranch?: (planId: string) => void;
  onDiscardBranch?: (planId: string) => void;
  onResolveConflicts?: (planId: string) => void;
  expanded: boolean; generating: boolean; executingChunkId: string | null; executingAll?: boolean; autoReplanThreshold: number;
}) {
  const [showLog, setShowLog] = useState<string | null>(null);
  const [showFileInfo, setShowFileInfo] = useState(false);
  const [selectedChunks, setSelectedChunks] = useState<Set<string>>(new Set());
  const chunks = (plan.chunks || []).sort((a, b) => a.sortOrder - b.sortOrder);

  const toggleChunkSelection = (chunkId: string) => {
    setSelectedChunks((prev) => {
      const next = new Set(prev);
      if (next.has(chunkId)) next.delete(chunkId);
      else next.add(chunkId);
      return next;
    });
  };

  const clearSelection = () => setSelectedChunks(new Set());

  // Bulk action helpers
  const selectedArr = [...selectedChunks];
  const selectedStatuses = chunks.filter((c) => selectedChunks.has(c.id)).map((c) => c.status);
  const canBulkSkip = selectedStatuses.some((s) => s === 'pending' || s === 'failed');
  const canBulkRetry = selectedStatuses.some((s) => s === 'failed');
  const canBulkUndefer = selectedStatuses.some((s) => s === 'deferred');

  // Find chunks that depend on a set of chunk IDs (for cascade skip)
  const getDependentChunkIds = (skipIds: Set<string>): string[] => {
    const skipTitles = new Set(chunks.filter((c) => skipIds.has(c.id)).map((c) => c.title));
    return chunks
      .filter((c) => !skipIds.has(c.id) && c.dependsOn?.some((dep: string) => skipTitles.has(dep)))
      .map((c) => c.id);
  };
  const doneCount = chunks.filter((c) => c.status === 'done').length;
  const totalChunks = chunks.length;
  const nextPending = chunks.find((c) => c.status === 'pending');
  const isStale = plan.status === 'stale';
  const isPlanning = plan.status === 'planning';
  const canExecute = plan.status === 'active' || plan.status === 'executing';

  return (
    <div class={`memo-card plan-card${expanded ? ' expanded' : ''}${isStale ? ' plan-stale' : ''}`} onClick={() => onExpand(plan.id)}>
      <div class="memo-card-header">
        <div class="memo-card-left">
          <span class="memo-status-dot" style={{ background: isStale ? '#f59e0b' : plan.status === 'active' ? '#10b981' : isPlanning ? '#3b82f6' : '#666' }} />
          <span class="group-name">{plan.projectKey}</span>
          {plan.name && plan.name !== 'Living Plan' && (
            <span class="plan-name-label"> / {plan.name}</span>
          )}
          <span class="plan-source-badge" title={plan.source === 'file' ? 'From plan file' : 'From voice memos'}>
            {plan.source === 'file' ? '\u{1F4C4}' : '\u{1F3A4}'}
          </span>
          <span class="memo-size">v{plan.version}{plan.source === 'memo' ? ` \u00B7 ${plan.totalMemos} memo${plan.totalMemos !== 1 ? 's' : ''}` : ''}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span class="memo-status-label">
            {totalChunks > 0 ? `${doneCount}/${totalChunks} CHUNKS` : 'no chunks'}
          </span>
          <button
            title="Plan file info"
            onClick={(e) => { e.stopPropagation(); setShowFileInfo((v) => !v); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', color: showFileInfo ? '#60a5fa' : '#666', fontSize: '13px', lineHeight: 1 }}
          >ℹ</button>
        </div>
      </div>
      {showFileInfo && (
        <div style={{ fontSize: '11px', color: '#9ca3af', padding: '2px 8px 4px', wordBreak: 'break-all' }} onClick={(e) => e.stopPropagation()}>
          {plan.planFilePath ? `📄 ${plan.planFilePath}` : '📭 DB only — no file yet'}
        </div>
      )}
      {(isStale || isPlanning) && (
        <div class="plan-badges-row">
          {isStale && (
            <span class="plan-stale-badge">{plan.pendingMemoCount || '?'}/{autoReplanThreshold} to auto-replan</span>
          )}
          {isPlanning && <span class="plan-planning-badge">re-planning...</span>}
        </div>
      )}

      <ProjectPills tags={[plan.projectKey]} />

      {/* Chunk list — always visible as compact summary */}
      {chunks.length > 0 && (
        <div class="plan-chunks-list">
          {/* Bulk actions toolbar */}
          {expanded && selectedChunks.size > 0 && (
            <div class="chunk-bulk-toolbar" onClick={(e) => e.stopPropagation()}>
              <span class="chunk-bulk-count">{selectedChunks.size} selected</span>
              {canBulkSkip && (
                <button class="chunk-btn chunk-btn-defer" onClick={() => {
                  const deps = getDependentChunkIds(selectedChunks);
                  if (deps.length > 0 && confirm(`${deps.length} chunk(s) depend on your selection. Skip those too?`)) {
                    onBulkChunkStatus([...selectedArr, ...deps], 'deferred');
                  } else {
                    onBulkChunkStatus(selectedArr, 'deferred');
                  }
                  clearSelection();
                }} title="Defer selected">Skip {selectedStatuses.filter((s) => s === 'pending' || s === 'failed').length}</button>
              )}
              {canBulkRetry && (
                <button class="chunk-btn chunk-btn-retry" onClick={() => {
                  for (const id of selectedArr) {
                    const c = chunks.find((ch) => ch.id === id);
                    if (c?.status === 'failed') onExecuteChunk(c.id, c.title);
                  }
                  clearSelection();
                }} title="Retry selected failed">Retry {selectedStatuses.filter((s) => s === 'failed').length}</button>
              )}
              {canBulkUndefer && (
                <button class="chunk-btn chunk-btn-exec" onClick={() => {
                  onBulkChunkStatus(selectedArr.filter((id) => chunks.find((c) => c.id === id)?.status === 'deferred'), 'pending');
                  clearSelection();
                }} title="Restore deferred to pending">Restore {selectedStatuses.filter((s) => s === 'deferred').length}</button>
              )}
              <button class="chunk-btn" onClick={clearSelection} title="Clear selection">&times;</button>
            </div>
          )}
          {chunks.slice(0, expanded ? chunks.length : 5).map((chunk) => (
            <div key={chunk.id} class={`plan-chunk-row chunk-${chunk.status}${selectedChunks.has(chunk.id) ? ' chunk-selected' : ''}`}>
              {expanded && (
                <input type="checkbox" class="chunk-checkbox"
                  checked={selectedChunks.has(chunk.id)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleChunkSelection(chunk.id)} />
              )}
              <span class="chunk-status-icon">
                {executingChunkId === chunk.id ? <span class="spinner-sm" /> : CHUNK_STATUS_ICONS[chunk.status] || '○'}
              </span>
              <span class="chunk-title">{chunk.title}</span>
              {chunk.effort && <span class="chunk-effort">{chunk.effort}</span>}
              <span class="chunk-risk-badge" style={{ background: RISK_COLORS[chunk.riskLevel] || '#666' }}>
                {chunk.riskLevel.toUpperCase()}
              </span>
              {/* Chunk actions when expanded */}
              {expanded && (
                <span class="chunk-actions" onClick={(e) => e.stopPropagation()}>
                  {chunk.status === 'pending' && (
                    <button class="chunk-btn chunk-btn-exec" disabled={!!executingChunkId}
                      onClick={() => onExecuteChunk(chunk.id, chunk.title)} title="Execute this chunk">
                      {executingChunkId === chunk.id ? '…' : '▶'}
                    </button>
                  )}
                  {chunk.status === 'needs-review' && (
                    <>
                      <button class="chunk-btn chunk-btn-approve" onClick={() => onChunkStatus(chunk.id, 'done')} title="Approve">✓</button>
                      <button class="chunk-btn chunk-btn-reject" onClick={() => onChunkStatus(chunk.id, 'failed')} title="Reject">✗</button>
                    </>
                  )}
                  {chunk.status === 'failed' && (
                    <button class="chunk-btn chunk-btn-retry" disabled={!!executingChunkId}
                      onClick={() => onExecuteChunk(chunk.id, chunk.title)} title="Retry">↻</button>
                  )}
                  {(chunk.status === 'pending' || chunk.status === 'failed') && (
                    <button class="chunk-btn chunk-btn-defer" onClick={() => onChunkStatus(chunk.id, 'deferred')} title="Defer">—</button>
                  )}
                  {chunk.status === 'deferred' && (
                    <button class="chunk-btn chunk-btn-exec" onClick={() => onChunkStatus(chunk.id, 'pending')} title="Restore to pending">↩</button>
                  )}
                  {chunk.executionLog && (
                    <button class="chunk-btn chunk-btn-log" onClick={() => setShowLog(showLog === chunk.id ? null : chunk.id)} title="View log">
                      {showLog === chunk.id ? '▼' : '▶'} log
                    </button>
                  )}
                </span>
              )}
            </div>
          ))}
          {!expanded && chunks.length > 5 && (
            <div class="plan-chunk-more">+{chunks.length - 5} more chunks</div>
          )}
        </div>
      )}

      {/* Execution log viewer */}
      {expanded && showLog && (() => {
        const logChunk = chunks.find((c) => c.id === showLog);
        return logChunk?.executionLog ? (
          <div class="chunk-execution-log" onClick={(e) => e.stopPropagation()}>
            <div class="chunk-log-header">Execution Log — {logChunk.title}</div>
            <pre class="chunk-log-content">{logChunk.executionLog}</pre>
            {onDiscussChunkResult && (
              <div class="chunk-log-actions">
                <button class="memo-btn memo-btn-architect" onClick={() => {
                  const context = `I'm reviewing the execution result for chunk **"${logChunk.title}"** in project **${plan.projectKey}** (${logChunk.riskLevel} risk, status: ${logChunk.status}).\n\n` +
                    `**Execution log:**\n\`\`\`\n${logChunk.executionLog!.slice(0, 3000)}\n\`\`\`\n\n` +
                    `Please review this result — was it done correctly? Any issues or follow-up needed?`;
                  onDiscussChunkResult(plan.projectKey, context);
                }} title="Discuss this result with the Architect">
                  🏗️ Discuss Result
                </button>
              </div>
            )}
          </div>
        ) : null;
      })()}

      {/* Full plan markdown when expanded */}
      {expanded && plan.currentPlan && (
        <div class="memo-plan">
          <div class="memo-plan-header">
            Full Plan v{plan.version}
            {plan.planModel && <span class="plan-model-badge">{plan.planModel}</span>}
            {plan.planCostUsd != null && <span class="plan-cost">${Number(plan.planCostUsd).toFixed(4)}</span>}
          </div>
          <MarkdownContent text={plan.currentPlan} class="memo-plan-content" />
        </div>
      )}

      <div class="memo-card-actions" onClick={(e) => e.stopPropagation()}>
        {nextPending && (
          <button class="memo-btn memo-btn-execute" disabled={!canExecute || !!executingChunkId}
            onClick={() => onExecuteChunk(nextPending.id, nextPending.title)}
            title={isStale ? 'Re-plan required before executing' : 'Execute next pending chunk'}>
            {executingChunkId ? '…' : '▶ Execute Next'}
          </button>
        )}
        {chunks.filter((c) => c.status === 'pending').length > 1 && (
          <button class="memo-btn memo-btn-execute-all" disabled={!canExecute || executingAll}
            onClick={() => onExecuteAll(plan.id)}
            title={isStale ? 'Re-plan required before executing' : 'Enqueue all pending chunks for autonomous execution'}>
            {executingAll ? '…' : '▶▶ Execute All'}
          </button>
        )}
        {chunks.some((c) => c.status === 'failed') && chunks.some((c) => c.status === 'pending') && (
          <button class="memo-btn memo-btn-skip-failures" disabled={!canExecute}
            onClick={() => onSkipFailuresAndContinue(plan.id)}
            title="Defer all failed chunks, then execute remaining pending">
            ⏭ Skip Failures
          </button>
        )}
        {onDiscussPlan && (
          <button class="memo-btn memo-btn-architect" onClick={() => {
            const chunkSummary = chunks.map((c) => `- [${c.status}] ${c.title} (${c.riskLevel})`).join('\n');
            const context = `I'm looking at the living plan for **${plan.projectKey}** (v${plan.version}, ${totalChunks} chunks, ${doneCount} done).\n\n` +
              `**Chunk summary:**\n${chunkSummary}\n\n` +
              (plan.currentPlan ? `**Plan excerpt:**\n${plan.currentPlan.slice(0, 1500)}\n\n` : '') +
              `Please review the plan status and share your thoughts — priorities, risks, or next steps.`;
            onDiscussPlan(plan.projectKey, context);
          }} title="Discuss this plan with the project's Architect">
            🏗️ Discuss
          </button>
        )}
        <button class="memo-btn memo-btn-process" onClick={() => onGenerate(plan.projectKey)} disabled={generating || isPlanning}
          title={isStale ? 'Re-plan to absorb new memos' : plan.version > 0 ? 'Re-plan (absorb new memos)' : 'Generate Plan'}>
          {generating || isPlanning ? '…' : isStale ? '🧠 Re-plan!' : plan.version > 0 ? '🧠 Re-plan' : '🧠 Plan'}
        </button>
        <button class="memo-btn memo-btn-delete" onClick={() => { if (confirm(`Delete plan for ${plan.projectKey}?`)) onDelete(plan.id); }} title="Delete Plan">🗑</button>
        {plan.dtmJobId && (
          <a class="memo-btn memo-btn-technical" href="https://dtm.akrsmv.net" target="_blank" rel="noopener"
            title="Open Pulse for live stack health">
            📊 Pulse
          </a>
        )}
      </div>
      {plan.executionBranch && chunks.every((c) => ['done', 'failed', 'deferred'].includes(c.status)) && (
        <div class="plan-branch-actions" onClick={(e) => e.stopPropagation()}>
          <span class="plan-branch-label" title={plan.executionBranch}>
            🌿 {plan.executionBranch}
          </span>
          {onMergeBranch && (
            <button class="memo-btn memo-btn-merge" onClick={() => onMergeBranch(plan.id)}
              title="Squash-merge this branch into main">
              🔀 Merge
            </button>
          )}
          {onResolveConflicts && (
            <button class="memo-btn memo-btn-resolve" onClick={() => onResolveConflicts(plan.id)}
              title="Spawn an agent to resolve merge conflicts with main">
              🔧 Resolve
            </button>
          )}
          {onDiscardBranch && (
            <button class="memo-btn memo-btn-discard" onClick={() => {
              if (confirm(`Discard branch ${plan.executionBranch}? All changes will be lost.`))
                onDiscardBranch(plan.id);
            }} title="Delete this branch and return to main">
              🗑️ Discard
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function MemosPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'memos' | 'groups' | 'plans'>('memos');
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [executingChunkId, setExecutingChunkId] = useState<string | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [audioEl] = useState(() => typeof Audio !== 'undefined' ? new Audio() : null);
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [toast, setToast] = useState<string | null>(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [editingTagsId, setEditingTagsId] = useState<string | null>(null);
  const [projectKeys, setProjectKeys] = useState<string[]>([]);
  const [autoReplanThreshold, setAutoReplanThreshold] = useState(5);

  // Fetch project keys for the tag picker + user settings for threshold
  useEffect(() => {
    authFetch('/api/projects')
      .then((r) => r.json())
      .then((tags: any[]) => setProjectKeys(tags.map((t: any) => t.key)))
      .catch(() => {});
    authFetch('/api/settings')
      .then((r) => r.json())
      .then((s: any) => { if (s?.memoSettings?.autoReplanThreshold) setAutoReplanThreshold(s.memoSettings.autoReplanThreshold); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const unsub = onAuthChange((user) => {
      const isAuth = !!user;
      setAuthenticated(isAuth);
      if (!isAuth) window.location.href = '/auth';
      setLoading(false);
    });
    return unsub;
  }, []);

  const [textMemo, setTextMemo] = useState('');
  const [textMemoSubmitting, setTextMemoSubmitting] = useState(false);

  const { connected, socketRef } = useSocket({ enabled: authenticated });
  const {
    memos, groups, plans, stats, loading: memosLoading,
    fetchMemos, fetchStats, fetchGroups, fetchPlans, saveMemo, saveTextMemo,
    transcribeMemo, processMemo, batchTranscribe,
    deleteMemo, updateMemo, retriageMemo, distillMemo, batchDistill,
    batchTriage, autoGroup, planGroup, planAllGroups, deleteGroup,
    promoteGroup, promoteMemo,
    generatePlan, deletePlan,
    executeChunk, updateChunkStatus, bulkUpdateChunkStatus, skipFailuresAndContinue,
    executeAllPending, fetchQueueStats, pauseQueue, resumeQueue,
    mergePlanBranch, discardPlanBranch, resolveConflicts,
    lastAutoPinEvent,
  } = useMemos({ socket: socketRef?.current ?? null, connected });

  const { recording, durationMs, start: startRecording, stop: stopRecording, micError, clearMicError } = useMemoRecorder();
  const {
    attachments, hasAttachments, processing: attachProcessing,
    triggerFilePicker, clearAttachments, fileInputProps,
  } = useAttachments();

  const STT_LANGS = [{ code: 'en', label: 'EN' }, { code: 'bg', label: 'BG' }, { code: '', label: 'Auto' }];
  // settings-zustand-migration Phase 2: legacy 'stt-language' migrated into the store.
  const sttLanguage = useSettingsStore((s) => s.sttLanguage);
  const cycleSttLanguage = useCallback(() => {
    const idx = STT_LANGS.findIndex((l) => l.code === sttLanguage);
    const next = STT_LANGS[(idx + 1) % STT_LANGS.length].code;
    useSettingsStore.getState().setSttLanguage(next);
  }, [sttLanguage]);

  const [bugs, setBugs] = useState<VoiceMemoSummary[]>([]);
  const fetchBugs = useCallback(async () => {
    try {
      const res = await authFetch('/api/memos?type=bug&limit=50');
      if (res.ok) setBugs((await res.json()).memos || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (authenticated) { fetchMemos(filter); fetchStats(); fetchGroups(); fetchPlans(); fetchBugs(); }
  }, [authenticated, filter]);

  const showToast = useCallback((msg: string) => {
    setToast(msg); setTimeout(() => setToast(null), 3000);
  }, []);

  // Show toast on auto-pin events
  useEffect(() => {
    if (!lastAutoPinEvent) return;
    const { pendingMemoCount, autoReplanThreshold, autoReplanTriggered } = lastAutoPinEvent;
    if (autoReplanTriggered) {
      showToast(`Auto-pinned + re-planning! (${pendingMemoCount}/${autoReplanThreshold})`);
    } else {
      showToast(`Auto-pinned (${pendingMemoCount}/${autoReplanThreshold} to auto-replan)`);
    }
  }, [lastAutoPinEvent, showToast]);

  const handleRecordToggle = useCallback(async () => {
    if (recording) {
      // Stop recording and submit
      const { chunks, durationMs: dur } = await stopRecording();
      if (chunks.length > 0) {
        const images = hasAttachments ? attachments.map((a) => ({ data: a.data, mimeType: a.mimeType, fileName: a.fileName })) : undefined;
        saveMemo(chunks, dur, images, sttLanguage || undefined);
        if (hasAttachments) clearAttachments();
        showToast(images?.length ? `Memo saved with ${images.length} image(s)` : 'Memo saved');
      }
    } else {
      // Start recording
      startRecording();
    }
  }, [recording, startRecording, stopRecording, saveMemo, showToast, hasAttachments, attachments, clearAttachments]);

  const handleSubmitTextMemo = useCallback(async () => {
    const t = textMemo.trim();
    if (!t) return;
    setTextMemoSubmitting(true);
    try {
      await saveTextMemo(t);
      setTextMemo('');
      showToast('Memo saved');
      fetchMemos(filter);
      fetchStats();
    } catch {
      showToast('Failed to save memo');
    } finally {
      setTextMemoSubmitting(false);
    }
  }, [textMemo, saveTextMemo, fetchMemos, fetchStats, filter]);

  const handleTranscribe = useCallback(async (id: string) => {
    setBusyId(id); await transcribeMemo(id); setBusyId(null); fetchStats();
  }, [transcribeMemo, fetchStats]);

  const handleProcess = useCallback(async (id: string) => {
    setBusyId(id); showToast('Adding to plan...');
    const r = await processMemo(id); setBusyId(null);
    if (r) {
      const threshold = r.autoReplanThreshold || 5;
      if (r.autoReplanTriggered) {
        showToast(`Re-planning now! (${r.pendingMemoCount}/${threshold} threshold reached)`);
        // Poll for plan completion (planning → active)
        const poll = setInterval(async () => {
          await fetchPlans();
        }, 3000);
        setTimeout(() => clearInterval(poll), 120000); // stop after 2min
      } else {
        showToast(r.planStale
          ? `Added (${r.pendingMemoCount}/${threshold} to auto-replan)`
          : 'Added to plan');
      }
      fetchPlans();
    } else {
      showToast('Failed to add to plan');
    }
  }, [processMemo, showToast, fetchPlans]);

  const handleBatchTranscribe = useCallback(async () => {
    showToast('Transcribing all...');
    const r = await batchTranscribe();
    if (r) showToast(`${r.transcribed} transcribed, ${r.errors} errors`);
  }, [batchTranscribe, showToast]);

  const handleRetriage = useCallback(async (id: string) => {
    setBusyId(id);
    showToast('Re-triaging...');
    const r = await retriageMemo(id);
    setBusyId(null);
    if (r) showToast(`Tagged: ${r.projectTags?.join(', ') || 'general'}`);
  }, [retriageMemo, showToast]);

  const handleSaveProjectTags = useCallback(async (id: string, tags: string[]) => {
    await updateMemo(id, { tags });
    setEditingTagsId(null);
    showToast(`Tags updated: ${tags.join(', ')}`);
  }, [updateMemo, showToast]);

  const handleBatchTriage = useCallback(async () => {
    showToast('Triaging...');
    const r = await batchTriage();
    if (r) showToast(`${r.triaged} triaged, ${r.errors} errors`);
  }, [batchTriage, showToast]);

  const handleBatchDistill = useCallback(async () => {
    showToast('Distilling...');
    const r = await batchDistill();
    if (r) showToast(`${r.distilled} distilled, ${r.errors} errors`);
  }, [batchDistill, showToast]);

  const handleDistill = useCallback(async (id: string) => {
    setBusyId(id);
    const r = await distillMemo(id);
    setBusyId(null);
    if (r) showToast('Distilled');
  }, [distillMemo, showToast]);

  const handleAutoGroup = useCallback(async () => {
    showToast('Grouping...');
    const r = await autoGroup();
    if (r) { showToast(`${r.count} groups created`); setTab('groups'); }
  }, [autoGroup, showToast]);

  const handlePlanGroup = useCallback(async (id: string) => {
    showToast('Generating group plan...');
    const r = await planGroup(id);
    if (r) { showToast('Group plan ready!'); setExpandedId(id); }
    else showToast('Planning failed');
  }, [planGroup, showToast]);

  const handlePlanAll = useCallback(async () => {
    const r = await planAllGroups((name, idx, total) => {
      showToast(`Planning ${name}... (${idx}/${total})`);
    });
    if (r) showToast(`Done! ${r.planned} planned, ${r.errors} errors`);
  }, [planAllGroups, showToast]);

  const handlePromoteGroup = useCallback(async (id: string) => {
    showToast('Creating session from plan...');
    const sessionId = await promoteGroup(id);
    if (sessionId) {
      showToast('Session created! Redirecting...');
      setTimeout(() => { window.location.href = '/'; }, 500);
    } else {
      showToast('Failed to create session');
    }
  }, [promoteGroup, showToast]);

  const handlePromoteMemo = useCallback(async (id: string) => {
    showToast('Creating session from plan...');
    const sessionId = await promoteMemo(id);
    if (sessionId) {
      showToast('Session created! Redirecting...');
      setTimeout(() => { window.location.href = '/'; }, 500);
    } else {
      showToast('Failed to create session');
    }
  }, [promoteMemo, showToast]);

  const handleGeneratePlan = useCallback(async (projectKey: string) => {
    setGeneratingPlan(true);
    showToast(`Generating plan for ${projectKey} (Opus)...`);
    const r = await generatePlan(projectKey, 'opus');
    setGeneratingPlan(false);
    if (r) { showToast(`Plan v${r.version} ready! (${r.chunks?.length || 0} chunks)`); setExpandedId(r.id); }
    else showToast('Plan generation failed');
  }, [generatePlan, showToast]);

  const handleExecuteChunk = useCallback(async (chunkId: string, chunkTitle: string) => {
    setExecutingChunkId(chunkId);
    showToast(`Executing "${chunkTitle}"...`);
    const result = await executeChunk(chunkId);
    setExecutingChunkId(null);
    if (result?.error) {
      showToast(`Failed: ${result.error}`);
    } else if (result?.status === 'done') {
      showToast(`"${chunkTitle}" completed!`);
    } else if (result?.status === 'needs-review') {
      showToast(`"${chunkTitle}" ready for review`);
    } else if (result?.status === 'failed') {
      showToast(`"${chunkTitle}" failed — check execution log`);
    }
  }, [executeChunk, showToast]);

  const handleChunkStatusChange = useCallback(async (chunkId: string, status: string) => {
    await updateChunkStatus(chunkId, status);
    showToast(`Chunk marked as ${status}`);
  }, [updateChunkStatus, showToast]);

  const handleBulkChunkStatus = useCallback(async (chunkIds: string[], status: string) => {
    const count = await bulkUpdateChunkStatus(chunkIds, status);
    showToast(`${count}/${chunkIds.length} chunks marked as ${status}`);
  }, [bulkUpdateChunkStatus, showToast]);

  const handleSkipFailuresAndContinue = useCallback(async (planId: string) => {
    showToast('Skipping failed chunks and re-executing...');
    const enqueued = await skipFailuresAndContinue(planId);
    if (enqueued > 0) showToast(`Failures skipped, ${enqueued} pending chunks enqueued`);
    else showToast('Failures skipped — no pending chunks to execute');
  }, [skipFailuresAndContinue, showToast]);

  const [executingAllPlanId, setExecutingAllPlanId] = useState<string | null>(null);
  const handleExecuteAll = useCallback(async (planId: string) => {
    if (executingAllPlanId) return; // prevent double-click
    setExecutingAllPlanId(planId);
    try {
      showToast('Enqueuing all pending chunks...');
      const count = await executeAllPending(planId);
      if (count > 0) showToast(`${count} chunks enqueued for autonomous execution`);
      else showToast('No chunks enqueued (queue disabled or none pending)');
    } finally {
      setExecutingAllPlanId(null);
    }
  }, [executeAllPending, showToast, executingAllPlanId]);

  const handleMergeBranch = useCallback(async (planId: string) => {
    showToast('Merging branch into main...');
    const result = await mergePlanBranch(planId);
    if (result.ok) {
      showToast('Branch merged successfully');
    } else {
      showToast(result.message || 'Merge failed');
    }
  }, [mergePlanBranch, showToast]);

  const handleDiscardBranch = useCallback(async (planId: string) => {
    showToast('Discarding branch...');
    const ok = await discardPlanBranch(planId);
    showToast(ok ? 'Branch discarded' : 'Discard failed');
  }, [discardPlanBranch, showToast]);

  const [resolvingPlanId, setResolvingPlanId] = useState<string | null>(null);
  const handleResolveConflicts = useCallback(async (planId: string) => {
    if (resolvingPlanId) return;
    setResolvingPlanId(planId);
    showToast('Spawning agent to resolve conflicts... (may take ~30s)');
    try {
      const result = await resolveConflicts(planId);
      showToast(result.ok ? 'Conflicts resolved — try Merge again' : (result.message || 'Resolution failed'));
    } finally {
      setResolvingPlanId(null);
    }
  }, [resolveConflicts, showToast, resolvingPlanId]);

  /** Navigate to main page and open an ephemeral session with contextual message */
  const handleDiscussWithArchitect = useCallback((projectKey: string, context: string) => {
    sessionStorage.setItem('discuss-context', JSON.stringify({
      projectKey,
      context,
    }));
    window.location.href = '/';
  }, []);

  const handlePlay = useCallback((id: string) => {
    if (!audioEl) return;
    if (playingId === id) { audioEl.pause(); audioEl.currentTime = 0; setPlayingId(null); return; }
    audioEl.src = `/api/memos/${id}/audio`;
    audioEl.onended = () => setPlayingId(null);
    audioEl.play().catch(() => setPlayingId(null));
    setPlayingId(id);
  }, [audioEl, playingId]);

  const handleExpand = useCallback((id: string) => setExpandedId((prev) => prev === id ? null : id), []);

  if (loading) return <div class="loading-screen"><div class="spinner" /></div>;

  const recordedCount = stats?.recorded || 0;
  const transcribedUntagged = memos.filter((m) => m.status === 'transcribed' && (!m.projectTags || m.projectTags.length === 0)).length;
  const ungroupedTagged = memos.filter((m) => !m.groupId && m.projectTags && m.projectTags.length > 0 && ['transcribed', 'planned'].includes(m.status)).length;
  const undistilled = memos.filter((m) => m.transcript && !m.distilledTranscript).length;
  const openGroups = groups.filter((g) => g.status === 'open').length;

  return (
    <div class="memos-page">
      <div class="memos-header">
        <div class="memos-header-left">
          <a href="/" class="memos-back">←</a>
          <div class="memos-header-title">
            <div class="board-mark" title="Board — voice captures, plans, roadmaps">
              <span class="board-c">B</span>
              <span class="board-c">o</span>
              <span class="board-c">a</span>
              <span class="board-c">r</span>
              <span class="board-c">d</span>
              <span class={`board-pulse ${connected ? 'on' : 'off'}`} title={connected ? 'Live updates on' : 'Offline'} />
            </div>
            <p class="board-tagline">Capture · plans · roadmaps</p>
          </div>
        </div>
        {stats && (
          <div class="memos-stats" onClick={() => setStatsOpen(!statsOpen)}>
            {stats.today.memos} today · ${stats.totalPlanCostUsd.toFixed(2)} total
            <span class="stats-chevron">{statsOpen ? '▲' : '▼'}</span>
          </div>
        )}
      </div>

      {/* Expandable stats dashboard */}
      {statsOpen && stats && (
        <div class="stats-dashboard">
          <div class="stats-row">
            <div class="stats-card">
              <div class="stats-card-value">{stats.today.memos}</div>
              <div class="stats-card-label">Today</div>
              <div class="stats-card-detail">{formatDuration(stats.today.durationMs)} · {stats.today.planned} planned</div>
            </div>
            <div class="stats-card">
              <div class="stats-card-value">{stats.week.memos}</div>
              <div class="stats-card-label">This Week</div>
              <div class="stats-card-detail">{formatDuration(stats.week.durationMs)} · {stats.week.planned} planned</div>
            </div>
            <div class="stats-card">
              <div class="stats-card-value">{stats.total}</div>
              <div class="stats-card-label">All Time</div>
              <div class="stats-card-detail">{formatDuration(stats.totalDurationMs)} · {formatSize(stats.totalSizeBytes)}</div>
            </div>
          </div>
          <div class="stats-row">
            <div class="stats-card cost">
              <div class="stats-card-value">${stats.today.costUsd.toFixed(2)}</div>
              <div class="stats-card-label">Today Cost</div>
              <div class="stats-card-detail">{stats.today.groups} group plan{stats.today.groups !== 1 ? 's' : ''}</div>
            </div>
            <div class="stats-card cost">
              <div class="stats-card-value">${stats.week.costUsd.toFixed(2)}</div>
              <div class="stats-card-label">Week Cost</div>
              <div class="stats-card-detail">{stats.week.groups} group plan{stats.week.groups !== 1 ? 's' : ''}</div>
            </div>
            <div class="stats-card cost">
              <div class="stats-card-value">${stats.totalPlanCostUsd.toFixed(2)}</div>
              <div class="stats-card-label">Total Cost</div>
              <div class="stats-card-detail">{stats.groupsPlanned} group{stats.groupsPlanned !== 1 ? 's' : ''} planned</div>
            </div>
          </div>
        </div>
      )}

      {/* Tab strip */}
      <div class="memos-tabs">
        <button class={`memos-tab${tab === 'memos' ? ' active' : ''}`} onClick={() => setTab('memos')}>
          Memos ({stats?.total || 0})
        </button>
        <button class={`memos-tab${tab === 'groups' ? ' active' : ''}`} onClick={() => { setTab('groups'); fetchGroups(); }}>
          Groups ({groups.length})
        </button>
        <button class={`memos-tab${tab === 'plans' ? ' active' : ''}`} onClick={() => { setTab('plans'); fetchPlans(); }}>
          Plans ({plans.length})
        </button>
      </div>

      {/* Batch action bar */}
      <div class="memos-filters">
        {tab === 'memos' ? (
          <>
            {[
              { label: 'All', value: undefined },
              { label: `Recorded (${stats?.recorded || 0})`, value: 'recorded' },
              { label: `Transcribed (${stats?.transcribed || 0})`, value: 'transcribed' },
              { label: `Planned (${stats?.planned || 0})`, value: 'planned' },
            ].map((f) => (
              <button key={f.label} class={`memo-filter-chip${filter === f.value ? ' active' : ''}`} onClick={() => setFilter(f.value)}>
                {f.label}
              </button>
            ))}
            {recordedCount > 0 && <button class="memo-filter-chip batch-btn" onClick={handleBatchTranscribe}>Transcribe All</button>}
            {transcribedUntagged > 0 && <button class="memo-filter-chip batch-btn" onClick={handleBatchTriage}>Triage ({transcribedUntagged})</button>}
            {undistilled > 0 && <button class="memo-filter-chip batch-btn" onClick={handleBatchDistill}>Distill ({undistilled})</button>}
            {ungroupedTagged > 0 && <button class="memo-filter-chip batch-btn" onClick={handleAutoGroup}>Group ({ungroupedTagged})</button>}
          </>
        ) : tab === 'groups' ? (
          <>
            {openGroups > 0 && <button class="memo-filter-chip batch-btn" onClick={handlePlanAll}>Plan All ({openGroups})</button>}
            {ungroupedTagged > 0 && <button class="memo-filter-chip batch-btn" onClick={handleAutoGroup}>Auto-Group ({ungroupedTagged})</button>}
          </>
        ) : (
          <>
            {/* Plans tab — show project keys that have memos but no plan yet */}
            {projectKeys
              .filter((k) => memos.some((m) => m.projectTags?.includes(k)) && !plans.some((p) => p.projectKey === k))
              .slice(0, 5)
              .map((k) => (
                <button key={k} class="memo-filter-chip batch-btn" onClick={() => handleGeneratePlan(k)} disabled={generatingPlan}>
                  + {k}
                </button>
              ))
            }
          </>
        )}
      </div>

      {/* Content */}
      <div class="memos-list">
        {tab === 'memos' ? (
          memosLoading && memos.length === 0 ? (
            <div class="memos-empty">Loading...</div>
          ) : memos.length === 0 ? (
            <div class="memos-empty">No memos yet. Hold the record button below to capture your first idea.</div>
          ) : (
            memos.map((memo) => (
              <MemoCard key={memo.id} memo={memo} onTranscribe={handleTranscribe} onProcess={handleProcess}
                onDelete={deleteMemo} onPlay={handlePlay} onExpand={handleExpand}
                onRetriage={handleRetriage} onEditTags={(id) => setEditingTagsId(id)}
                onDistill={handleDistill}
                playingId={playingId} busyId={busyId} expanded={expandedId === memo.id} />
            ))
          )
        ) : tab === 'groups' ? (
          groups.length === 0 ? (
            <div class="memos-empty">No groups yet. Triage your memos first, then tap "Auto-Group".</div>
          ) : (
            groups.map((group) => (
              <GroupCard key={group.id} group={group} onPlan={handlePlanGroup}
                onPromote={handlePromoteGroup}
                onAskArchitect={group.projectTags?.length === 1 ? handleDiscussWithArchitect : undefined}
                onDelete={deleteGroup} onExpand={handleExpand} expanded={expandedId === group.id} />
            ))
          )
        ) : (
          plans.length === 0 ? (
            <div class="memos-empty">No living plans yet. Record memos, triage them, then generate a plan from the buttons above.</div>
          ) : (
            plans.map((plan) => (
              <PlanCard key={plan.id} plan={plan} onGenerate={handleGeneratePlan}
                onDelete={deletePlan} onExpand={handleExpand}
                onExecuteChunk={handleExecuteChunk} onChunkStatus={handleChunkStatusChange}
                onBulkChunkStatus={handleBulkChunkStatus}
                onExecuteAll={handleExecuteAll}
                onSkipFailuresAndContinue={handleSkipFailuresAndContinue}
                onDiscussPlan={handleDiscussWithArchitect}
                onDiscussChunkResult={handleDiscussWithArchitect}
                onMergeBranch={handleMergeBranch}
                onDiscardBranch={handleDiscardBranch}
                onResolveConflicts={handleResolveConflicts}
                expanded={expandedId === plan.id}
                generating={generatingPlan} executingChunkId={executingChunkId}
                executingAll={executingAllPlanId === plan.id}
                autoReplanThreshold={autoReplanThreshold} />
            ))
          )
        )}
      </div>

      {/* Bugs section */}
      {bugs.length > 0 && (
        <div class="memos-bugs-section">
          <div class="memos-bugs-header">
            <span class="memos-bugs-title">🐞 Bugs</span>
            <span class="memo-filter-chip">{bugs.length}</span>
          </div>
          <div class="memos-list">
            {bugs.map((bug) => (
              <MemoCard
                key={bug.id}
                memo={bug}
                onTranscribe={handleTranscribe}
                onProcess={handleProcess}
                onDelete={async (id) => { await deleteMemo(id); fetchBugs(); }}
                onPlay={handlePlay}
                onExpand={handleExpand}
                onRetriage={handleRetriage}
                onEditTags={(id) => setEditingTagsId(id)}
                onDistill={handleDistill}
                playingId={playingId}
                busyId={busyId}
                expanded={expandedId === bug.id}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hidden file input for image picker */}
      <input {...fileInputProps} />

      {/* Attachment preview */}
      {hasAttachments && (
        <div class="memo-attach-preview">
          {attachments.map((a, i) => (
            <div key={i} class="memo-attach-thumb">
              {a.thumbnailDataUri && <img src={a.thumbnailDataUri} alt={a.fileName} />}
              <button class="memo-attach-remove" onClick={() => {
                const updated = attachments.filter((_, j) => j !== i);
                if (updated.length === 0) clearAttachments();
                // Note: useAttachments doesn't expose removeAttachment by index easily,
                // so we just show them; they clear on record
              }}>✕</button>
            </div>
          ))}
          <span class="memo-attach-hint">{attachments.length} image{attachments.length !== 1 ? 's' : ''} — will attach to next recording</span>
        </div>
      )}

      {/* Mic error banner */}
      {micError && (
        <div class="memo-mic-error" onClick={clearMicError}>
          {micError} <span class="memo-mic-error-close">✕</span>
        </div>
      )}

      {/* Text memo input */}
      <div class="memo-text-row">
        <textarea
          class="memo-text-input"
          placeholder="Type a memo..."
          value={textMemo}
          rows={2}
          onInput={(e) => setTextMemo((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleSubmitTextMemo(); } }}
        />
        <button
          class="memo-text-send"
          onClick={handleSubmitTextMemo}
          disabled={!textMemo.trim() || textMemoSubmitting}
          title="Save text memo (Ctrl+Enter)"
        >{textMemoSubmitting ? '…' : '↑'}</button>
      </div>

      {/* STT language picker — hidden while recording */}
      {!recording && (
        <div class="stt-lang-row">
          {STT_LANGS.map((l) => (
            <button
              key={l.code}
              class={`stt-lang-btn${sttLanguage === l.code ? ' active' : ''}`}
              onClick={() => useSettingsStore.getState().setSttLanguage(l.code)}
              type="button"
              title={l.code === '' ? 'Auto-detect language' : `Transcribe in ${l.label}`}
            >{l.label}</button>
          ))}
        </div>
      )}

      {/* Record FAB + Attach button */}
      <div class="memo-fab-container">
        <button class="memo-attach-btn" onClick={triggerFilePicker} disabled={attachProcessing} title="Attach image">
          📎
        </button>
        <button class={`memo-fab${recording ? ' recording' : ''}`}
          onClick={handleRecordToggle}>
          <span class="memo-fab-label">{recording ? `⏹ ${formatDuration(durationMs)}` : hasAttachments ? `🎙 Tap to record (${attachments.length} 📎)` : '🎙 Tap to record'}</span>
        </button>
      </div>

      {toast && <div class="memo-toast">{toast}</div>}

      {/* Tag picker modal */}
      {editingTagsId && (() => {
        const memo = memos.find((m) => m.id === editingTagsId);
        const currentTags = memo?.projectTags || [];
        return (
          <div class="tag-picker-overlay" onClick={() => setEditingTagsId(null)}>
            <div class="tag-picker-modal" onClick={(e) => e.stopPropagation()}>
              <div class="tag-picker-header">
                <span>Edit Project Tags</span>
                <button class="tag-picker-close" onClick={() => setEditingTagsId(null)}>✕</button>
              </div>
              <div class="tag-picker-list">
                {projectKeys.map((key) => {
                  const selected = currentTags.includes(key);
                  return (
                    <label key={key} class={`tag-picker-item${selected ? ' selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {
                          const newTags = selected
                            ? currentTags.filter((t) => t !== key)
                            : [...currentTags, key];
                          handleSaveProjectTags(editingTagsId!, newTags);
                        }}
                      />
                      <span>{key}</span>
                    </label>
                  );
                })}
              </div>
              <div class="tag-picker-actions">
                <button class="memo-btn" onClick={() => {
                  handleRetriage(editingTagsId!);
                  setEditingTagsId(null);
                }}>🏷 Re-triage with AI</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
